import { useCallback, useEffect, useState } from "react";
import {
  crearInteresado,
  actualizarInteresado,
  eliminarInteresado,
  obtenerInteresado,
  buscarPorDocumento,
} from "../../../../services/interesadosService";
import {
  normalizeInteresado,
  buildInteresadoPayload,
} from "../adapters/interesadosAdapter";
import { initialInteresadoForm } from "../constants/interesados";
import { showToast } from "../../../../utils/showToast";
import empresasService from "../../../../services/empresasService";
import referentesService from "../../../../services/referentesService";

const normalizeText = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const resolveTipoReferido = (claveFuente = "") => {
  if (!claveFuente) return "";
  if (claveFuente === "docente") return "docente";
  if (["estudiante", "administrativo", "externo"].includes(claveFuente)) return claveFuente;
  return "";
};

const resolveFuenteClave = (form = {}, fuentesOptions = []) => {
  const selectedId = form.fuente_origen_id;
  const option = fuentesOptions.find((f) => String(f.value) === String(selectedId));
  const nombre = option?.label || form.fuente_origen_nombre || "";
  return normalizeText(nombre);
};

const validar = (form) => {
  if (!form.nombres.trim()) return "Ingresa los nombres del interesado.";
  if (!form.telefono.trim()) return "Ingresa el telefono del interesado.";
  const telefonoLimpio = form.telefono.trim();
  if (telefonoLimpio && !/^09\d{8}$/.test(telefonoLimpio)) {
    return "El telefono debe iniciar con 09 y tener 10 digitos.";
  }
  const doc = (form.documento || "").trim();
  if (doc && doc.length < 5) return "La cedula debe tener al menos 5 digitos.";
  return null;
};

export default function useInteresadoForm({
  onCreated,
  onUpdated,
  onDeleted,
  fuentesOptions = [],
  empresasOptions = [],
  existentes = [],
}) {
  const [modal, setModal] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [buscandoDoc, setBuscandoDoc] = useState(false);
  const [formNuevo, setFormNuevo] = useState(initialInteresadoForm);
  const [formEditar, setFormEditar] = useState(initialInteresadoForm);
  const [interesadoEditando, setInteresadoEditando] = useState(null);
  const [referidosOpciones, setReferidosOpciones] = useState([]);
  const [referidoSearchTerm, setReferidoSearchTerm] = useState("");
  const [cargandoReferidos, setCargandoReferidos] = useState(false);

  const formActivo = modal === "editar" ? formEditar : formNuevo;
  const fuenteActivaClave = resolveFuenteClave(formActivo, fuentesOptions);
  const tipoReferidoActivo = resolveTipoReferido(fuenteActivaClave);
  const fuenteActivaHabilitaReferido = ["administrativo", "estudiante", "docente"].includes(fuenteActivaClave);

  const mapReferenteOption = useCallback((referido, { persist = false } = {}) => {
    if (!referido) return null;
    return {
      id: referido.id,
      nombres: referido.nombres || referido.nombre || referido.referido_nombres || "",
      apellidos: referido.apellidos || referido.referido_apellidos || "",
      documento_identidad:
        referido.documento_identidad ||
        referido.documento ||
        referido.identificacion ||
        referido.referido_documento ||
        "",
      estado_academico: referido.estado_academico || referido.tipo || referido.referido_estado || "",
      persist,
    };
  }, []);

  const findOptionLabel = useCallback((options, value) => {
    if (value === null || value === undefined || value === "") return "";
    const option = options.find((opt) => String(opt.value) === String(value));
    return option?.label || "";
  }, []);

  const findEmpresaByName = useCallback(
    (nombre) => {
      const normalizado = normalizeText(nombre);
      if (!normalizado) return null;
      const option = (empresasOptions || []).find(
        (emp) => normalizeText(emp.label) === normalizado
      );
      return option ? { id: option.value, nombre: option.label } : null;
    },
    [empresasOptions]
  );

  const existeDocumento = useCallback(
    (doc, omitId = null) => {
      if (!doc) return false;
      const cedula = String(doc).trim();
      if (!cedula) return false;
      return (existentes || []).some(
        (i) =>
          String(i.documento || i.documento_identidad || "").trim() === cedula &&
          (omitId === null || String(i.id) !== String(omitId))
      );
    },
    [existentes]
  );

  const includeReferidoOption = useCallback((referido) => {
    const preferFilled = (current, next) => {
      if (next === undefined || next === null) return current;
      if (typeof next === "string" && next.trim() === "") return current;
      return next;
    };
    const nextOption = mapReferenteOption(referido, { persist: true });
    if (!nextOption?.id) return;

    setReferidosOpciones((prev) => {
      const idx = prev.findIndex((ref) => String(ref.id) === String(nextOption.id));

      if (idx === -1) {
        return [...prev, nextOption];
      }

      const current = prev[idx];
      const merged = {
        ...current,
        ...nextOption,
        nombres: preferFilled(current.nombres, nextOption.nombres),
        apellidos: preferFilled(current.apellidos, nextOption.apellidos),
        documento_identidad: preferFilled(current.documento_identidad, nextOption.documento_identidad),
        estado_academico: preferFilled(current.estado_academico, nextOption.estado_academico),
        persist: current.persist || nextOption.persist,
      };

      const updated = [...prev];
      updated[idx] = merged;
      return updated;
    });
  }, [mapReferenteOption]);

  const buildFormFromInteresado = useCallback((data) => {
    const parsed = normalizeInteresado(data);
    return {
      nombres: parsed.nombres || parsed.nombreCompleto || "",
      apellidos: parsed.apellidos || "",
      documento: parsed.documento || "",
      telefono: parsed.telefono || "",
      email: parsed.email || "",
      fuente_origen_id: parsed.fuenteOrigenId ? String(parsed.fuenteOrigenId) : "",
      fuente_origen_nombre: parsed.fuenteOrigen || "",
      empresa_origen_id: parsed.empresaOrigenId ? String(parsed.empresaOrigenId) : "",
      empresa_nombre: parsed.empresaOrigen || "",
      nueva_empresa_nombre: "",
      referido_por_id: parsed.referidoPorId ?? "",
      estado_interes: parsed.estadoInteres || "nuevo",
    };
  }, []);

  const buscarDocumento = useCallback(
    async (tipo = "nuevo") => {
      const currentForm = tipo === "editar" ? formEditar : formNuevo;
      const documento = (currentForm.documento || "").trim();
      if (!documento) {
        showToast({ type: "warning", message: "Ingresa la cedula para buscar." });
        return;
      }
      setBuscandoDoc(true);
      try {
        const found = await buscarPorDocumento(documento);
        if (found) {
          const nextForm = buildFormFromInteresado(found);
          if (found.referido_por_id) {
            includeReferidoOption({
              id: found.referido_por_id,
              nombres: found.referido_nombres || "",
              apellidos: found.referido_apellidos || "",
              documento_identidad: found.referido_documento || "",
              estado_academico: found.referido_estado || "",
            });
          }
          if (tipo === "editar") {
            setInteresadoEditando((prev) => prev || normalizeInteresado(found));
            setFormEditar((prev) => ({ ...prev, ...nextForm }));
          } else {
            setFormNuevo((prev) => ({ ...prev, ...nextForm }));
          }
          showToast({ type: "success", message: "Interesado encontrado y cargado." });
        } else {
          showToast({ type: "warning", message: "No se encontro un interesado con esa cedula." });
        }
      } catch (err) {
        if (err?.status !== 404) console.error(err);
        const message =
          err?.status === 404
            ? "No se encontro un interesado con esa cedula."
            : "No se pudo buscar el interesado.";
        showToast({ type: "warning", message });
      } finally {
        setBuscandoDoc(false);
      }
    },
    [formEditar, formNuevo, buildFormFromInteresado, includeReferidoOption]
  );

  const abrirCrear = useCallback(() => {
    setFormNuevo(initialInteresadoForm);
    setReferidosOpciones([]);
    setReferidoSearchTerm("");
    setModal("crear");
  }, []);

  const abrirEditar = useCallback(
    async (interesado) => {
      if (!interesado) return;
      setLoadingDetalle(true);
      try {
        const id = interesado.id ?? interesado;
        const detalle = await obtenerInteresado(id);
        const parsed = normalizeInteresado(detalle, id);
        setInteresadoEditando(parsed);
        setFormEditar({
          nombres: parsed.nombres || parsed.nombreCompleto || "",
          apellidos: parsed.apellidos || "",
          documento: parsed.documento || "",
          telefono: parsed.telefono || "",
          email: parsed.email || "",
          fuente_origen_id: parsed.fuenteOrigenId ? String(parsed.fuenteOrigenId) : "",
          fuente_origen_nombre: parsed.fuenteOrigen || "",
          empresa_origen_id: parsed.empresaOrigenId ? String(parsed.empresaOrigenId) : "",
          empresa_nombre: parsed.empresaOrigen || "",
          nueva_empresa_nombre: "",
          referido_por_id: parsed.referidoPorId ?? "",
          estado_interes: parsed.estadoInteres || "nuevo",
        });
        if (parsed.referidoPorId) {
          const baseRef = {
            id: parsed.referidoPorId,
            nombres: parsed.referidoNombre || parsed.referidoDocumento || "",
            apellidos: "",
            documento_identidad: parsed.referidoDocumento || "",
            estado_academico: parsed.referidoEstado || "",
          };
          includeReferidoOption(baseRef);
          const missingNames = !baseRef.nombres && !baseRef.apellidos && !baseRef.documento_identidad;
          referentesService
            .obtener(parsed.referidoPorId)
            .then((info) => {
              if (!info) return;
              includeReferidoOption({
                id: parsed.referidoPorId,
                nombre: info.nombre || "",
                identificacion: info.identificacion || "",
                tipo: info.tipo || "",
              });
            })
            .catch(() => null);
          if (missingNames && baseRef.documento_identidad) {
            includeReferidoOption({
              id: parsed.referidoPorId,
              nombres: baseRef.documento_identidad,
              apellidos: "",
              documento_identidad: baseRef.documento_identidad,
              estado_academico: baseRef.estado_academico || "",
            });
          }
        }
        setModal("editar");
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: "No se pudo cargar el interesado a editar." });
      } finally {
        setLoadingDetalle(false);
      }
    },
    [includeReferidoOption]
  );

  const abrirEliminar = useCallback((interesado) => {
    if (!interesado) return;
    setInteresadoEditando(normalizeInteresado(interesado));
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setInteresadoEditando(null);
    setFormNuevo(initialInteresadoForm);
    setFormEditar(initialInteresadoForm);
    setReferidosOpciones([]);
    setReferidoSearchTerm("");
  }, []);

  const resolverEmpresa = useCallback(
    async (form) => {
      if (form.empresa_origen_id) {
        const nombre = findOptionLabel(empresasOptions, form.empresa_origen_id) || form.empresa_nombre || "";
        return { id: form.empresa_origen_id, nombre };
      }
      const nombreLibre = (form.empresa_nombre || form.nueva_empresa_nombre || "").trim();
      if (!nombreLibre) return { id: null, nombre: "" };
      const existente = findEmpresaByName(nombreLibre);
      if (existente) return existente;

      const creada = await empresasService
        .crear({
          nombre: nombreLibre,
          ruc: null,
          telefono: null,
          email: null,
          tipo: "externa",
          activo: 1,
        })
        .catch((err) => {
          console.error("No se pudo crear la empresa:", err);
          return null;
        });

      if (!creada?.id) {
        throw new Error("No se pudo crear la empresa. Intenta nuevamente.");
      }
      return { id: creada.id, nombre: creada.nombre || nombreLibre };
    },
    [empresasOptions, findOptionLabel, findEmpresaByName]
  );

  useEffect(() => {
    let active = true;
    const term = (referidoSearchTerm || "").trim();
    const minChars = 2;

    if (!modal || !fuenteActivaHabilitaReferido || term.length < minChars) {
      const keepIds = new Set(
        [formNuevo.referido_por_id, formEditar.referido_por_id]
          .filter(Boolean)
          .map((id) => String(id))
      );
      setReferidosOpciones((prev) =>
        prev.filter((ref) => ref.id && (ref.persist || keepIds.has(String(ref.id))))
      );
      setCargandoReferidos(false);
      return () => {
        active = false;
      };
    }

    setCargandoReferidos(true);
    const handler = setTimeout(async () => {
      try {
        const tipoFiltro = tipoReferidoActivo || undefined;
        const lista = await referentesService
          .buscar(term, { tipo: tipoFiltro })
          .catch(() => []);
        if (active) {
          const opciones = (Array.isArray(lista) ? lista : [])
            .map((ref) => mapReferenteOption(ref))
            .filter(Boolean);
          const selectedId =
            modal === "editar"
              ? String(formEditar.referido_por_id || "")
              : String(formNuevo.referido_por_id || "");

          setReferidosOpciones((prev) => {
            if (!selectedId) return opciones;
            const selectedPrev = prev.find((opt) => String(opt.id) === selectedId);
            const alreadyIncluded = opciones.some((opt) => String(opt.id) === selectedId);
            if (!selectedPrev || alreadyIncluded) return opciones;
            return [selectedPrev, ...opciones];
          });
        }
      } catch (err) {
        console.error("No se pudo recuperar los referidos:", err);
        if (active) setReferidosOpciones([]);
      } finally {
        if (active) setCargandoReferidos(false);
      }
    }, 350);

    return () => {
      active = false;
      clearTimeout(handler);
    };
  }, [
    referidoSearchTerm,
    formNuevo.referido_por_id,
    formEditar.referido_por_id,
    mapReferenteOption,
    modal,
    fuenteActivaHabilitaReferido,
    tipoReferidoActivo,
  ]);

  useEffect(() => {
    if (!modal || fuenteActivaHabilitaReferido) return;

    const clearReferido = (setForm) =>
      setForm((prev) => {
        if (!prev.referido_por_id) return prev;
        return { ...prev, referido_por_id: "" };
      });

    if (modal === "editar") clearReferido(setFormEditar);
    if (modal === "crear") clearReferido(setFormNuevo);

    setReferidoSearchTerm((prev) => (prev ? "" : prev));
    setReferidosOpciones((prev) => (prev.length ? [] : prev));
    setCargandoReferidos(false);
  }, [modal, fuenteActivaHabilitaReferido]);

  const handleCrear = useCallback(async () => {
    const validation = validar(formNuevo);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    const doc = (formNuevo.documento || "").trim();
    if (doc && existeDocumento(doc)) {
      showToast({ type: "warning", message: "Ya existe un interesado con esa cedula." });
      return;
    }
    setSaving(true);
    try {
      const empresaResuelta = await resolverEmpresa(formNuevo);
      const payload = buildInteresadoPayload(formNuevo);
      payload.fuente_origen_id = payload.fuente_origen_id || null;
      payload.empresa_origen_id = empresaResuelta.id ? Number(empresaResuelta.id) : null;
      payload.referido_por_id = payload.referido_por_id || null;
      const fuenteNombre =
        findOptionLabel(fuentesOptions, payload.fuente_origen_id) || formNuevo.fuente_origen_nombre;
      const creada = await crearInteresado(payload);
      const normalized = normalizeInteresado(
        {
          ...formNuevo,
          ...payload,
          ...(creada || {}),
          id: creada?.id ?? creada?.insertId,
          fuente_origen: fuenteNombre,
          empresa_referencia: empresaResuelta.nombre,
        },
        creada?.id
      );
      onCreated?.(normalized);
      showToast({ type: "success", message: "Interesado creado correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo crear el interesado." });
    } finally {
      setSaving(false);
    }
  }, [formNuevo, onCreated, cerrarModal, findOptionLabel, fuentesOptions, resolverEmpresa, existeDocumento]);

  const handleActualizar = useCallback(async () => {
    if (!interesadoEditando) return;
    const validation = validar(formEditar);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    const doc = (formEditar.documento || "").trim();
    if (doc && existeDocumento(doc, interesadoEditando.id)) {
      showToast({ type: "warning", message: "Ya existe otro interesado con esa cedula." });
      return;
    }
    setSaving(true);
    try {
      const empresaResuelta = await resolverEmpresa(formEditar);
      const payload = buildInteresadoPayload(formEditar);
      payload.fuente_origen_id = payload.fuente_origen_id || null;
      payload.empresa_origen_id = empresaResuelta.id ? Number(empresaResuelta.id) : null;
      payload.referido_por_id = payload.referido_por_id || null;
      const fuenteNombre =
        findOptionLabel(fuentesOptions, payload.fuente_origen_id) || formEditar.fuente_origen_nombre;
      const actualizada = await actualizarInteresado(interesadoEditando.id, payload);
      const normalized = normalizeInteresado({
        ...interesadoEditando,
        ...formEditar,
        ...payload,
        ...(actualizada || {}),
        fuente_origen: fuenteNombre || interesadoEditando.fuenteOrigen,
        empresa_referencia: empresaResuelta.nombre || interesadoEditando.empresaOrigen,
      });
      onUpdated?.(normalized);
      showToast({ type: "success", message: "Interesado actualizado correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo actualizar el interesado." });
    } finally {
      setSaving(false);
    }
  }, [interesadoEditando, formEditar, onUpdated, cerrarModal, resolverEmpresa, findOptionLabel, fuentesOptions, existeDocumento]);

  const handleEliminar = useCallback(async () => {
    if (!interesadoEditando) return;
    setDeleting(true);
    try {
      await eliminarInteresado(interesadoEditando.id);
      onDeleted?.(interesadoEditando);
      showToast({ type: "success", message: "Interesado eliminado correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo eliminar el interesado." });
    } finally {
      setDeleting(false);
    }
  }, [interesadoEditando, onDeleted, cerrarModal]);

  return {
    modal,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    interesadoEditando,
    saving,
    deleting,
    loadingDetalle,
    buscandoDoc,
    abrirCrear,
    abrirEditar,
    abrirEliminar,
    cerrarModal,
    handleCrear,
    handleActualizar,
    handleEliminar,
    referidosOpciones,
    referidoSearchTerm,
    setReferidoSearchTerm,
    cargandoReferidos,
    buscarDocumento,
  };
}
